/**
 * The composer's Read / Write / Plan mode — what each one means on the wire, as pure data.
 *
 * ── WHAT THE BACKEND ACTUALLY DOES WITH `mode_hint` (traced, not assumed) ─────────────────────
 *
 * `POST /api/runs {mode_hint}` (multi-step-agentic `routers/runs.py` `RunCreate`) is persisted on
 * the run row and echoed back as `RunState.run.mode`. Only ONE value changes execution:
 *
 *   plan   `emit_code`/`emit_permission_change` leave the tool roster, the run is nudged to call
 *          `emit_plan`, ends `plan_proposed`, writes a `plan_doc` artifact and raises a non-blocking
 *          `approve_plan`. (`reactive_worker.py` plan-mode roster; `services/plan_doc.py`.)
 *   write  Labels the run, and at close `promotion.py` checks the run produced a change (code, a
 *          change plan, or a typed decline) — a write-declared run that produced nothing gets a
 *          `MISSING_REQUIRED_EMIT` gap. Tool selection is NOT changed by the hint.
 *   read   Labels the run. Nothing else: the read-vs-write classifier (`graph/write_intent.py`)
 *          never reads the hint, and the contract's `FORBIDDEN_EMITS[("read")]` row is not
 *          enforced. A Read run asked to change code will change code.
 *
 * Absent and `"read"` are wire-identical for execution, and a run created without a hint is
 * projected as `read` anyway. So Read SENDS NOTHING — which also matters for the rule below.
 *
 * ── THE ONE THING THAT MUST NEVER SEND A HINT ───────────────────────────────────────────────
 *
 * A message typed while the run is parked on a clarification is carried INTO that question by the
 * backend (`runs.py` pending-interaction carry) — but only when the body carries no `mode_hint`
 * (nor attachments, `resume_from`, `plan`, …). With a hint present the carry bails out, a NEW run
 * starts, and the parked one hangs until it expires. `runCreateOptionsFor` therefore omits the
 * hint whenever the run's status is `input_required`, whatever mode is selected.
 *
 * Pure so the send-path rules are testable in the node suite: the wrong branch here is invisible
 * in a screenshot and only shows up as a question that was never answered.
 */

import type { Mode, RunStatus } from "./protocol";

/** The three modes the composer offers. Same vocabulary as the wire `Mode`, on purpose. */
export type ComposerMode = Mode;

export const COMPOSER_MODES: readonly ComposerMode[] = ["read", "write", "plan"];

export const DEFAULT_COMPOSER_MODE: ComposerMode = "read";

export interface ComposerModeCopy {
  label: string;
  /** One line, honest about what the backend does — see the module header. */
  description: string;
  /** Placeholder for the textarea while this mode is selected. */
  placeholder: string;
}

export const COMPOSER_MODE_COPY: Record<ComposerMode, ComposerModeCopy> = {
  read: {
    label: "Read",
    description: "Answer questions and analyse the org.",
    placeholder: "Ask about your org, or describe what to change…",
  },
  write: {
    label: "Write",
    description: "Make the change. Weave authors code or metadata and reports if it could not.",
    placeholder: "Describe the change to make…",
  },
  plan: {
    label: "Plan",
    description: "Propose a plan first. Nothing changes until you approve it.",
    placeholder: "Describe the change — Weave proposes a plan and waits for your approval",
  },
};

export function isComposerMode(value: unknown): value is ComposerMode {
  return typeof value === "string" && (COMPOSER_MODES as readonly string[]).includes(value);
}

/**
 * Why the mode cannot be applied to the next message, or `null` when it can.
 *
 * Only one reason exists today: the run is waiting on a question, and the next message IS the
 * answer (see the header). Surfaced as copy on the picker so the reader is told, not surprised.
 */
export function composerModeLock(runStatus: RunStatus | null | undefined): string | null {
  return runStatus === "input_required" ? "Your next message answers the question above." : null;
}

/**
 * The `createRun` options for one message.
 *
 * Returns `undefined` — not `{}` — when nothing is to be sent, so the call site can spread it
 * without a `mode_hint: undefined` key reaching `JSON.stringify` (which drops it anyway, but a
 * body that is byte-identical to a hint-less request is easier to reason about in a capture).
 */
export function runCreateOptionsFor(
  mode: ComposerMode,
  runStatus: RunStatus | null | undefined,
): { mode_hint?: "read" | "write" | "plan" } | undefined {
  if (composerModeLock(runStatus)) return undefined;
  if (mode === "read") return undefined;
  return { mode_hint: mode };
}

/**
 * The mode to fall back to once a plan is approved and its execute run starts.
 *
 * Plan mode is a way of asking, not a place to stay: the run that executes the approved plan is a
 * WRITE run (the backend forces it), and the reader's next message is a follow-up on that work.
 * Staying in Plan would answer "now also add the validation rule" with another proposal. Same
 * behaviour as an editor's plan mode ending on approval.
 */
export function nextModeAfterPlanApproved(current: ComposerMode): ComposerMode {
  return current === "plan" ? "write" : current;
}
